import clsx from "clsx";
import type {
  BridgeSort,
  FilterState,
  SiteFilter,
  StatusFilter,
} from "../hooks/useFilters";

interface FilterBarProps {
  filters: FilterState;
  onChangeSite: (v: SiteFilter) => void;
  onChangeStatus: (v: StatusFilter) => void;
  onChangeSort: (v: BridgeSort) => void;
  onReset: () => void;
}

// ─── Option tables ────────────────────────────────────────────────────────────

const SITE_OPTIONS: ReadonlyArray<{ value: SiteFilter; label: string }> = [
  { value: "all", label: "전체 사이트" },
  { value: "hoban", label: "호반워치마일" },
  { value: "ansan", label: "안산워치마일" },
];

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "전체 상태" },
  { value: "UP", label: "정상 (UP)" },
  { value: "DOWN", label: "데이터 정체 (DOWN)" },
  { value: "STALE", label: "미운영 (STALE)" },
  { value: "RETIRED", label: "운영 종료 (RETIRED)" },
  { value: "UNKNOWN", label: "확인 중 (UNKNOWN)" },
];

const SORT_OPTIONS: ReadonlyArray<{ value: BridgeSort; label: string }> = [
  { value: "triage", label: "기본 (정체 → 정상 오래된 순 → 미운영)" },
  { value: "updated-oldest", label: "오래된 갱신 순" },
  { value: "updated-newest", label: "최신 갱신 순" },
  { value: "name-asc", label: "이름 가나다 오름차순" },
  { value: "name-desc", label: "이름 가나다 내림차순" },
  { value: "status-down-first", label: "장애 우선" },
];

// ─── Shared select styles ─────────────────────────────────────────────────────

const SELECT_CLASS = clsx(
  // 모바일 44px + 16px 글자(iOS 자동 확대 방지), 데스크톱은 기존 36px/14px 유지.
  "h-11 w-full text-base sm:h-9 sm:w-auto sm:text-sm",
  "rounded-md border bg-surface px-3",
  "border-surface-border text-slate-700",
  "dark:border-surface-darkBorder dark:bg-surface-darkSubtle dark:text-slate-200",
  "focus:outline-none focus:ring-2 focus:ring-status-operational/40",
);

/**
 * Page-level filter/sort controls. Renders three selects (site / status / sort)
 * plus a reset link. State lives in the parent (`StatusPage`) via `useFilters`.
 */
export default function FilterBar({
  filters,
  onChangeSite,
  onChangeStatus,
  onChangeSort,
  onReset,
}: FilterBarProps) {
  return (
    <div
      className={clsx(
        "flex flex-col gap-3 rounded-2xl border bg-surface px-4 py-3 shadow-sm",
        "border-surface-border dark:border-surface-darkBorder dark:bg-surface-darkSubtle",
        "sm:flex-row sm:flex-wrap sm:items-end sm:gap-4 sm:px-5",
      )}
      aria-label="필터 및 정렬"
    >
      <div className="flex flex-col gap-1">
        <label
          htmlFor="filter-site"
          className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          사이트
        </label>
        <select
          id="filter-site"
          className={SELECT_CLASS}
          value={filters.site}
          onChange={(e) => onChangeSite(e.target.value as SiteFilter)}
        >
          {SITE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="filter-status"
          className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          상태
        </label>
        <select
          id="filter-status"
          className={SELECT_CLASS}
          value={filters.status}
          onChange={(e) => onChangeStatus(e.target.value as StatusFilter)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label
          htmlFor="filter-sort"
          className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          정렬
        </label>
        <select
          id="filter-sort"
          className={SELECT_CLASS}
          value={filters.sort}
          onChange={(e) => onChangeSort(e.target.value as BridgeSort)}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="ml-auto flex items-end">
        <button
          type="button"
          onClick={onReset}
          className={clsx(
            "h-11 rounded-md px-3 text-xs font-medium underline-offset-2 transition sm:h-9",
            "text-slate-500 hover:text-slate-700 hover:underline",
            "dark:text-slate-400 dark:hover:text-slate-200",
          )}
        >
          기본값으로 리셋
        </button>
      </div>
    </div>
  );
}
